import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Check, CreditCard, ArrowLeft, Loader2 } from "lucide-react";
import { getPlansArray, Plan } from "@shared/plans";
import { useCheckout } from "@/hooks/useCheckout";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { handleCheckout, loading } = useCheckout();

  // Get plan code from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const planCode = urlParams.get('plan') || 'PRO';

  const plans = getPlansArray();
  const selectedPlan: Plan | undefined = plans.find(p => p.code === planCode);

  if (!selectedPlan) {
    return (
      <DashboardLayout>
        <div className="space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold">Plan Not Found</h1>
            <p className="text-xl text-muted-foreground">
              The selected plan could not be found. Please go back and select a valid plan.
            </p>
            <Button onClick={() => navigate('/pricing')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Pricing
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const priceRupees = selectedPlan.monthlyPricePaise / 100;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">Complete Your Upgrade</h1>
          <p className="text-xl text-muted-foreground">
            You're one step away from unlocking advanced AI writing features.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Order Summary</span>
              {selectedPlan.code === "PRO" && (
                <Badge variant="default">Most Popular</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Review your selected plan before proceeding to payment
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Plan Details */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{selectedPlan.name} Plan</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">
                    {priceRupees === 0 ? "Free" : `₹${priceRupees}`}
                  </div>
                  {priceRupees > 0 && (
                    <div className="text-sm text-muted-foreground">per month</div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500" />
                  {selectedPlan.monthlyCreditGrant.toLocaleString()} monthly credits
                </div>
                {selectedPlan.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Billing Info */}
            {priceRupees > 0 && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Plan Price</span>
                  <span>₹{priceRupees}</span>
                </div>
                <div className="flex justify-between">
                  <span>Taxes & Fees</span>
                  <span>Included</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>₹{priceRupees}/month</span>
                </div>
              </div>
            )}

            {/* Benefits Reminder */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">What you get:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Instant access to premium AI models</li>
                <li>• {selectedPlan.monthlyCreditGrant.toLocaleString()} credits credited immediately</li>
                <li>• Priority customer support</li>
                <li>• Advanced writing templates and tools</li>
                <li>• Cancel anytime, no long-term commitment</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Plans
              </Button>

              <Button
                onClick={() => handleCheckout(selectedPlan)}
                disabled={loading || selectedPlan.code === "FREE"}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Proceed to Payment
                  </>
                )}
              </Button>
            </div>

            {/* Security Notice */}
            <div className="text-center text-sm text-muted-foreground">
              <p>Your payment is secured by Razorpay with 256-bit SSL encryption</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
